import { useAuth } from "@/context/AuthContext";

/**
 * Auth gate state machine for in-page useEffect data fetches.
 *
 * Pages that fetch data conditional on `profile?.id` historically had
 * a hang bug: they initialized `loading=true`, started a useEffect that
 * early-returned when profile was null, and never resolved the loading
 * gate. If a session expired, the auth refresh hung, or the profile fetch
 * silently failed, the page stayed on the spinner forever.
 *
 * This hook returns one of three coherent states so pages can render
 * the right thing without hand-rolling the gate:
 *
 *   "auth_loading"     → AuthContext is still resolving (show spinner)
 *   "session_expired"  → Auth resolved, no profile (render <SessionExpiredState/>)
 *   "ready"            → Auth resolved, profile present (fetch + render)
 */
export function useAuthGate() {
  const { profile, loading } = useAuth();
  if (loading) return { status: "auth_loading", profile: null };
  if (!profile?.id) return { status: "session_expired", profile: null };
  return { status: "ready", profile };
}
